import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BookOpen, User, Loader2, ArrowRight, ShoppingCart } from "lucide-react";

const Dashboard = () => {
  const { user, token } = useAuth();
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLibrary = async () => {
      try {
        const response = await axios.get(`${API}/user/library`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLibrary(response.data);
      } catch (error) {
        console.error("Failed to fetch library", error);
      } finally {
        setLoading(false);
      }
    };
    fetchLibrary();
  }, [token]);

  const handleDownload = async (bookId, bookTitle) => {
    try {
      const response = await fetch(`${API}/user/read/${bookId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 403) {
          toast.error("You do not have access to this book.");
        } else if (response.status === 401) {
          toast.error("Please sign in again to read this book.");
        } else {
          toast.error("Failed to open the book. Please try again.");
        }
        return;
      }

      const html = await response.text();
      const blob = new Blob([html], { type: "text/html" });
      const blobUrl = URL.createObjectURL(blob);
      const win = window.open(blobUrl, "_blank");
      if (win) {
        // Free the Blob URL once the new tab has had time to load.
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
        toast.success(`Opening ${bookTitle}. Use Print → Save as PDF to keep a copy.`);
      } else {
        URL.revokeObjectURL(blobUrl);
        toast.error("Please allow pop-ups to open the reader.");
      }
    } catch (error) {
      toast.error("Failed to open the book. Please try again.");
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-32" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center">
              <User size={24} className="text-gold" />
            </div>
            <div>
              <span className="text-[#A1A1AA] text-sm">Welcome back,</span>
              <h1 className="font-heading text-2xl text-[#EAEAEA]" data-testid="user-name">
                {user?.name}
              </h1>
            </div>
          </div>
        </div>

        {/* My Library */}
        <div className="mb-16" data-testid="user-library">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-3xl text-[#EAEAEA] flex items-center gap-3">
              <BookOpen size={28} className="text-gold" />
              My Library
            </h2>
            <Link to="/library">
              <Button variant="outline" className="border-white/20 text-[#A1A1AA] hover:border-gold/30 hover:text-gold rounded-none uppercase text-xs tracking-widest">
                <ShoppingCart size={14} className="mr-2" />
                Browse More
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-gold" size={32} />
            </div>
          ) : library.length === 0 ? (
            <div className="text-center py-20 border border-white/5 rounded-sm" data-testid="empty-library">
              <BookOpen size={48} className="mx-auto text-[#A1A1AA] mb-4" />
              <h3 className="font-heading text-2xl text-[#EAEAEA] mb-2">Your library is empty</h3>
              <p className="text-[#A1A1AA] mb-6">Start your journey by exploring the Somalume collection.</p>
              <Link to="/library">
                <Button className="bg-gold text-[#050505] hover:bg-gold-accent rounded-none uppercase text-xs tracking-widest">
                  Explore Library
                  <ArrowRight size={14} className="ml-2" />
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="library-books">
              {library.map((purchase) => (
                <div 
                  key={purchase.id} 
                  className="p-6 border border-white/5 rounded-sm bg-paper hover:border-gold/20 transition-colors duration-300"
                  data-testid={`library-book-${purchase.book_id}`}
                >
                  <h3 className="font-heading text-xl text-[#EAEAEA] mb-2">
                    {purchase.book_title}
                  </h3>
                  <p className="text-[#A1A1AA] text-sm mb-4">
                    Purchased {new Date(purchase.purchased_at).toLocaleDateString()}
                  </p>
                  <Button
                    onClick={() => handleDownload(purchase.book_id, purchase.book_title)}
                    variant="outline"
                    className="w-full border-gold/30 text-gold hover:bg-gold/10 rounded-none uppercase text-xs tracking-widest"
                    data-testid={`read-${purchase.book_id}`}
                  >
                    <BookOpen size={14} className="mr-2" />
                    Read
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Account Info */}
        <div className="border-t border-white/5 pt-12">
          <h2 className="font-heading text-2xl text-[#EAEAEA] mb-6">Account</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 border border-white/5 rounded-sm">
              <span className="text-[#A1A1AA] text-sm uppercase tracking-widest block mb-2">Email</span>
              <span className="text-[#EAEAEA]" data-testid="user-email">{user?.email}</span>
            </div>
            <div className="p-6 border border-white/5 rounded-sm">
              <span className="text-[#A1A1AA] text-sm uppercase tracking-widest block mb-2">Member Since</span>
              <span className="text-[#EAEAEA]" data-testid="user-since">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
